import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, Typography, Box, Button, Avatar, Stack, Divider, TextField, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Tooltip, IconButton } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PhoneIcon from '@mui/icons-material/Phone';
import StarIcon from '@mui/icons-material/Star';
import SchoolIcon from '@mui/icons-material/School';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { toast } from 'react-toastify';
import StatusChip from '../../components/StatusChip';
import ConfirmDialog from '../../components/ConfirmDialog';
import { formatCurrency, formatDate, formatPhone } from '../../utils/formatters';
import { getAstrologerDetail, updateAstrologerStatus, approveKYC, updateCommissionOverride, toggleProfileBoost, getAstrologerEarnings, deleteAstrologer, updateAstrologer } from '../../api/endpoints/astrologers';
import { uploadFile } from '../../api/endpoints/media';
import RevenueTrend from '../../components/charts/RevenueTrend';
import { API_URL } from '../../utils/url';

const AstrologerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [astrologer, setAstrologer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commission, setCommission] = useState('');
  const [showCommission, setShowCommission] = useState(false);
  const [kycDialogOpen, setKycDialogOpen] = useState(false);
  const [kycAction, setKycAction] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [kycProcessing, setKycProcessing] = useState(false);
  const [boostDialogOpen, setBoostDialogOpen] = useState(false);
  const [boostDays, setBoostDays] = useState(7);
  const [boostProcessing, setBoostProcessing] = useState(false);
  const [earnings, setEarnings] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteProcessing, setDeleteProcessing] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', niceName: '', photos: [] });
  const [editPhotoFiles, setEditPhotoFiles] = useState([]);
  const [editPhotoPreviews, setEditPhotoPreviews] = useState([]);
  const [editSaving, setEditSaving] = useState(false);

  const handleDelete = async () => {
    try {
      setDeleteProcessing(true);
      await deleteAstrologer(id);
      toast.success('Astrologer and all associated data permanently deleted');
      navigate('/astrologers', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setDeleteProcessing(false);
      setDeleteDialogOpen(false);
    }
  };

  const openEditDialog = () => {
    const name = astrologer.userId?.name || astrologer.name || '';
    setEditForm({
      name,
      niceName: astrologer.niceName || '',
      photos: astrologer.photos || [],
    });
    setEditPhotoFiles([]);
    setEditPhotoPreviews([]);
    setEditDialogOpen(true);
  };

  const handleEditPhotoAdd = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (editForm.photos.length + editPhotoFiles.length >= 10) {
      toast.warning('Max 10 photos');
      return;
    }
    setEditPhotoFiles(p => [...p, file]);
    const reader = new FileReader();
    reader.onload = (ev) => setEditPhotoPreviews(p => [...p, ev.target.result]);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleEditPhotoRemove = (index) => {
    if (index < editForm.photos.length) {
      setEditForm(p => ({ ...p, photos: p.photos.filter((_, i) => i !== index) }));
    } else {
      const fileIdx = index - editForm.photos.length;
      setEditPhotoFiles(p => p.filter((_, i) => i !== fileIdx));
      setEditPhotoPreviews(p => p.filter((_, i) => i !== fileIdx));
    }
  };

  const handleEditSave = async () => {
    if (!editForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setEditSaving(true);
    try {
      let photos = [...editForm.photos];
      const newUrls = await Promise.all(
        editPhotoFiles.map(f => uploadFile(f).then(r => r.data?.data?.url || r.data?.url).catch(() => null))
      );
      photos = [...photos, ...newUrls.filter(Boolean)];
      await updateAstrologer(id, {
        name: editForm.name.trim(),
        niceName: editForm.niceName.trim(),
        photos,
      });
      toast.success('Astrologer updated');
      setEditDialogOpen(false);
      fetchAstrologer();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update');
    } finally {
      setEditSaving(false);
    }
  };

  useEffect(() => {
    fetchAstrologer();
  }, [id]);

  useEffect(() => {
    if (astrologer) {
      getAstrologerEarnings(id, { period: '30d' })
        .then(res => setEarnings(res.data.data))
        .catch(() => {});
    }
  }, [astrologer, id]);

  const fetchAstrologer = async () => {
    try {
      setLoading(true);
      const res = await getAstrologerDetail(id);
      setAstrologer(res.data.data);
    } catch (err) {
      toast.error('Failed to load astrologer details');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async () => {
    try {
      await updateAstrologerStatus(id, !astrologer.isActive);
      setAstrologer((prev) => ({ ...prev, isActive: !prev.isActive }));
      toast.success(`Astrologer ${astrologer.isActive ? 'deactivated' : 'activated'}`);
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  const openKYCDialog = (status) => {
    setKycAction(status);
    setRejectReason('');
    setKycDialogOpen(true);
  };

  const handleKYCAction = async () => {
    if (kycAction === 'rejected' && (!rejectReason || rejectReason.trim().length < 3)) {
      toast.error('Please provide a valid reason for rejection (min 3 characters)');
      return;
    }
    try {
      setKycProcessing(true);
      await approveKYC(id, kycAction, kycAction === 'rejected' ? rejectReason : '');
      setAstrologer((prev) => ({ ...prev, kycStatus: kycAction }));
      toast.success(`KYC ${kycAction} successfully`);
      setKycDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.message || `Failed to ${kycAction} KYC`);
    } finally {
      setKycProcessing(false);
    }
  };

  const handleBoostToggle = async (boosted) => {
    try {
      setBoostProcessing(true);
      await toggleProfileBoost(id, boosted ? { boosted: true, boostDays } : { boosted: false });
      toast.success(boosted ? 'Profile boosted successfully' : 'Boost removed');
      setBoostDialogOpen(false);
      fetchAstrologer();
    } catch (err) {
      toast.error('Failed to update boost');
    } finally {
      setBoostProcessing(false);
    }
  };

  const handleCommissionSave = async () => {
    const val = Number(commission);
    if (!commission || val < 0 || val > 100) {
      toast.error('Commission must be between 0 and 100');
      return;
    }
    try {
      await updateCommissionOverride(id, { commissionRate: val });
      toast.success('Commission override updated');
      setShowCommission(false);
      fetchAstrologer();
    } catch (err) {
      toast.error('Failed to update commission');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!astrologer) {
    return <Typography>Astrologer not found</Typography>;
  }

  const name = astrologer.userId?.name || astrologer.name;
  const phone = astrologer.userId?.phone || astrologer.phone;
  const avatarUrl = API_URL?.API_BASE_URL+ astrologer.userId?.avatar ||API_URL?.API_BASE_URL+ astrologer.avatar;

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 2 }}>
        <Button startIcon={<ArrowBackIcon sx={{ fontSize: 28 }} />} onClick={() => navigate('/astrologers')}>
          Back to Astrologers
        </Button>
        <Tooltip title="Permanently delete this astrologer and all associated data">
          <Button
            variant="outlined"
            color="error"
            size="small"
            startIcon={<DeleteForeverIcon />}
            onClick={() => setDeleteDialogOpen(true)}
          >
            Delete Account
          </Button>
        </Tooltip>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar src={avatarUrl} sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: 'primary.main', fontSize: 32 }}>
                {avatarUrl ? '' : name.split(' ').map(n => n[0]).join('')}
              </Avatar>
              <Typography variant="h6">{name}</Typography>
              {astrologer.niceName && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>"{astrologer.niceName}"</Typography>}
              <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={openEditDialog}>Edit Profile</Button>
              <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 1 }}>
                <StatusChip status={astrologer.kycStatus} label={`KYC: ${astrologer.kycStatus}`} />
                <StatusChip status={astrologer.isLive ? 'online' : 'offline'} />
              </Stack>
              <Divider sx={{ my: 2 }} />
              <Stack spacing={1.5} alignItems="flex-start">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PhoneIcon fontSize="small" color="action" />
                  <Typography variant="body2">{formatPhone(phone)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <SchoolIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    <strong>Main:</strong> {Array.isArray(astrologer.mainCategories) ? astrologer.mainCategories.join(', ') : '-'} | <strong>Sub:</strong> {Array.isArray(astrologer.specializations) ? astrologer.specializations.join(', ') : astrologer.specializations || '-'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon fontSize="small" sx={{ color: '#FFD700' }} />
                  <Typography variant="body2">{(astrologer.rating || 0).toFixed(1)} Rating | {astrologer.experience || 0} yrs exp</Typography>
                </Box>
              </Stack>
              {astrologer.photos?.length > 0 && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>Photos</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1 }}>
                    {astrologer.photos.map((url, i) => (
                      <Box key={i} sx={{ aspectRatio: '1', borderRadius: 1.5, overflow: 'hidden', border: '1px solid #eee' }}>
                        <img src={API_URL?.API_BASE_URL + url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6" sx={{ color: 'primary.main' }}>{formatCurrency(astrologer.totalEarnings || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">Total Earnings</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6">{astrologer.totalChats || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Chats</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6">{astrologer.totalCalls || 0}</Typography>
                <Typography variant="caption" color="text.secondary">Calls</Typography>
              </CardContent></Card>
            </Grid>
            <Grid item xs={6} sm={3}>
              <Card><CardContent sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h6">{formatCurrency(astrologer.totalRevenue || 0)}</Typography>
                <Typography variant="caption" color="text.secondary">Revenue Generated</Typography>
              </CardContent></Card>
            </Grid>
          </Grid>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>KYC Status</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Current status: <StatusChip status={astrologer.kycStatus} />
              </Typography>
              {astrologer.kycStatus !== 'approved' && (
                <Stack direction="row" spacing={1}>
                  <Button variant="contained" color="success" size="small" onClick={() => openKYCDialog('approved')}>Approve KYC</Button>
                  <Button variant="contained" color="error" size="small" onClick={() => openKYCDialog('rejected')}>Reject KYC</Button>
                </Stack>
              )}
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Profile Boosting</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Current: {astrologer.profileBoosted ? <span style={{ color: '#4CAF50', fontWeight: 600 }}>Boosted</span> : 'Not Boosted'}
                {astrologer.profileBoostExpiry && <span style={{ color: '#666', marginLeft: 8 }}>Expires: {new Date(astrologer.profileBoostExpiry).toLocaleDateString()}</span>}
              </Typography>
              <Stack direction="row" spacing={1}>
                {astrologer.profileBoosted ? (
                  <Button variant="outlined" color="error" size="small" onClick={() => handleBoostToggle(false)}>Remove Boost</Button>
                ) : (
                  <Button variant="contained" color="warning" size="small" onClick={() => setBoostDialogOpen(true)}>Boost Profile</Button>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Commission Override</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Default commission: {astrologer.commissionRate || 70}%
                {astrologer.commissionOverride != null && (
                  <> <span style={{ color: '#F44336' }}>(Overridden: {astrologer.commissionOverride}%)</span></>
                )}
              </Typography>
              {showCommission ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField
                    size="small"
                    type="number"
                    value={commission}
                    onChange={(e) => setCommission(e.target.value)}
                    placeholder="Enter commission %"
                    sx={{ width: 180 }}
                    InputProps={{ endAdornment: <Typography variant="body2">%</Typography> }}
                  />
                  <Button variant="contained" size="small" onClick={handleCommissionSave}>Save</Button>
                  <Button variant="text" size="small" onClick={() => setShowCommission(false)}>Cancel</Button>
                </Stack>
              ) : (
                <Button variant="outlined" size="small" onClick={() => setShowCommission(true)}>Set Override</Button>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Earnings (Last 30 Days)</Typography>
              <RevenueTrend data={earnings?.dailyEarnings || earnings || []} />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={boostDialogOpen} onClose={() => !boostProcessing && setBoostDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Boost Profile</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Set the number of days to boost this astrologer's profile. Boosted profiles appear first in search results.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="number"
            label="Boost Duration (days)"
            value={boostDays}
            onChange={(e) => setBoostDays(Math.max(1, parseInt(e.target.value) || 1))}
            InputProps={{ inputProps: { min: 1, max: 365 } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBoostDialogOpen(false)} disabled={boostProcessing}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={() => handleBoostToggle(true)} disabled={boostProcessing} startIcon={boostProcessing ? <CircularProgress size={16} /> : null}>
            {boostProcessing ? 'Processing...' : 'Boost'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={kycDialogOpen} onClose={() => !kycProcessing && setKycDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {kycAction === 'approved' ? 'Approve KYC' : 'Reject KYC'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {kycAction === 'approved'
              ? 'Are you sure you want to approve this KYC?'
              : 'Please provide a reason for rejecting this KYC application.'}
          </Typography>
          {kycAction === 'rejected' && (
            <TextField
              autoFocus
              fullWidth
              multiline
              rows={3}
              label="Rejection Reason"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              error={rejectReason.length > 0 && rejectReason.trim().length < 3}
              helperText={rejectReason.length > 0 && rejectReason.trim().length < 3 ? 'At least 3 characters' : ''}
              sx={{ mb: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKycDialogOpen(false)} disabled={kycProcessing}>Cancel</Button>
          <Button
            variant="contained"
            color={kycAction === 'approved' ? 'success' : 'error'}
            onClick={handleKYCAction}
            disabled={kycProcessing}
            startIcon={kycProcessing ? <CircularProgress size={16} /> : null}
          >
            {kycProcessing ? 'Processing...' : (kycAction === 'approved' ? 'Approve' : 'Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Astrologer?"
        message="This will permanently delete this astrologer's account, all uploaded documents, and associated data. This action cannot be undone."
        confirmText="Delete Forever"
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        severity="error"
        loading={deleteProcessing}
      />

      <Dialog open={editDialogOpen} onClose={() => !editSaving && setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Astrologer</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              value={editForm.name}
              onChange={(e) => setEditForm(p => ({ ...p, name: e.target.value }))}
            />
            <TextField
              label="Nice Name"
              fullWidth
              value={editForm.niceName}
              onChange={(e) => setEditForm(p => ({ ...p, niceName: e.target.value }))}
              placeholder="e.g. AstroStar, Vedic Guru"
            />
            <Box>
              <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Photos ({editForm.photos.length + editPhotoFiles.length}/10)
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
                {editForm.photos.map((url, i) => (
                  <Box key={`existing-${i}`} sx={{ position: 'relative', display: 'inline-block' }}>
                    <img src={API_URL?.API_BASE_URL + url} alt="" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid #eee' }} />
                    <IconButton size="small" sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'white' }}
                      onClick={() => handleEditPhotoRemove(i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {editPhotoPreviews.map((preview, i) => (
                  <Box key={`new-${i}`} sx={{ position: 'relative', display: 'inline-block' }}>
                    <img src={preview} alt="" style={{ width: 80, height: 80, borderRadius: 8, objectFit: 'cover', border: '1px solid #eee' }} />
                    <IconButton size="small" sx={{ position: 'absolute', top: -6, right: -6, bgcolor: 'white' }}
                      onClick={() => handleEditPhotoRemove(editForm.photos.length + i)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                {editForm.photos.length + editPhotoFiles.length < 10 && (
                  <Button variant="outlined" component="label" sx={{ width: 80, height: 80, borderRadius: 2, borderStyle: 'dashed', minWidth: 80 }}>
                    <input type="file" accept="image/*" hidden onChange={handleEditPhotoAdd} />
                    <CloudUploadIcon />
                  </Button>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={editSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditSave} disabled={editSaving} startIcon={editSaving ? <CircularProgress size={16} /> : null}>
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AstrologerDetail;